// err-breadcrumbs-v1 — pure crash-snapshot sanitizer.
//
// Input: a serialized DOM string (document.documentElement.outerHTML of the
// crashed page). Output: a privacy/XSS-scrubbed copy safe to store and later
// download from the admin error-ticket UI:
//   - ALL <script> blocks removed (incl. unclosed/stray tags)
//   - inline event handlers (on*) stripped
//   - javascript: URLs neutralized
//   - every value="…" attribute, <textarea> content and [data-sensitive]
//     element content redacted to ••• (form input must NEVER leak)
//   - bearer/cookie/token material scrubbed as a final pass
//   - hard cap (<= 150KB) with an explicit truncation marker comment
//
// String-based on purpose: it runs in the vitest node-env suite (no DOM) as
// the deploy-gating guard, and acts as the FINAL authority after the DOM-side
// clone scrub in src/reliability.ts (belt and braces — e.g. nested
// [data-sensitive] trees are fully handled clone-side, flat cases here).
// MUST NEVER THROW: the crash path calls it while the app is already dying.

export const SNAPSHOT_MAX_LENGTH = 150_000 // chars ≈ bytes for our mostly-ASCII markup, kept under the 150KB contract cap
export const SNAPSHOT_REDACTED = '•••'
export const SNAPSHOT_TRUNCATION_MARKER = '<!-- err-snapshot-truncated err-breadcrumbs-v1 -->'
export const SNAPSHOT_SANITIZE_FAILED = '<!-- err-snapshot-sanitize-failed err-breadcrumbs-v1 -->'

const SCRIPT_BLOCK_RE = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi
const SCRIPT_OPEN_TAIL_RE = /<script\b[\s\S]*$/i // unclosed <script> swallows the rest of the doc — drop it all
const SCRIPT_STRAY_CLOSE_RE = /<\/script\s*>/gi
const HANDLER_DQ_RE = /\son[a-z]+\s*=\s*"[^"]*"/gi
const HANDLER_SQ_RE = /\son[a-z]+\s*=\s*'[^']*'/gi
const HANDLER_UQ_RE = /\son[a-z]+\s*=\s*[^\s>]+/gi
const JS_URL_DQ_RE = /(\s(?:href|src|action|formaction)\s*=\s*")\s*javascript:[^"]*(")/gi
const JS_URL_SQ_RE = /(\s(?:href|src|action|formaction)\s*=\s*')\s*javascript:[^']*(')/gi
const JS_URL_UQ_RE = /(\s(?:href|src|action|formaction)\s*=\s*)javascript:[^\s>]+/gi
const VALUE_DQ_RE = /(\svalue\s*=\s*)"[^"]*"/gi
const VALUE_SQ_RE = /(\svalue\s*=\s*)'[^']*'/gi
const VALUE_UQ_RE = /(\svalue\s*=\s*)[^\s>"'][^\s>]*/gi
const TEXTAREA_RE = /(<textarea\b[^>]*>)[\s\S]*?(<\/textarea\s*>)/gi
// Same-tag non-greedy close: fully covers flat [data-sensitive] usage; nested
// same-tag trees are pre-redacted by the DOM-side clone scrub.
const DATA_SENSITIVE_RE = /(<([a-z][a-z0-9-]*)\b[^>]*\bdata-sensitive\b[^>]*>)[\s\S]*?(<\/\2\s*>)/gi
// Mirrors src/reliability.ts SENSITIVE_VALUE_RE (no length slice here).
const AUTH_MATERIAL_RE = /\b(Bearer\s+)[A-Za-z0-9._~+/=-]+|(?:CF_Authorization|Authorization|access_token|refresh_token|id_token|client_secret)=([^;&\s"'<>]+)/gi

function capSnapshot(html: string, maxLength: number): string {
  if (html.length <= maxLength) return html
  if (maxLength <= SNAPSHOT_TRUNCATION_MARKER.length) return SNAPSHOT_TRUNCATION_MARKER.slice(0, Math.max(0, maxLength))
  return html.slice(0, maxLength - SNAPSHOT_TRUNCATION_MARKER.length) + SNAPSHOT_TRUNCATION_MARKER
}

export function sanitizeSnapshotHtml(html: unknown, maxLength = SNAPSHOT_MAX_LENGTH): string {
  try {
    let out = ''
    try {
      out = typeof html === 'string' ? html : String(html ?? '')
    } catch {
      return SNAPSHOT_SANITIZE_FAILED
    }

    // 1. Scripts gone (paired, then any unclosed tail, then stray closers).
    out = out.replace(SCRIPT_BLOCK_RE, '')
    out = out.replace(SCRIPT_OPEN_TAIL_RE, '')
    out = out.replace(SCRIPT_STRAY_CLOSE_RE, '')

    // 2. Inline handlers gone (quoted forms first so the unquoted pass only
    //    sees leftovers), then javascript: URLs neutralized.
    out = out.replace(HANDLER_DQ_RE, '')
    out = out.replace(HANDLER_SQ_RE, '')
    out = out.replace(HANDLER_UQ_RE, '')
    out = out.replace(JS_URL_DQ_RE, '$1#$2')
    out = out.replace(JS_URL_SQ_RE, '$1#$2')
    out = out.replace(JS_URL_UQ_RE, '$1#')

    // 3. Form input redacted: every value= attribute (any element — bias to
    //    privacy), textarea content, [data-sensitive] content.
    out = out.replace(VALUE_DQ_RE, `$1"${SNAPSHOT_REDACTED}"`)
    out = out.replace(VALUE_SQ_RE, `$1"${SNAPSHOT_REDACTED}"`)
    out = out.replace(VALUE_UQ_RE, `$1"${SNAPSHOT_REDACTED}"`)
    out = out.replace(TEXTAREA_RE, `$1${SNAPSHOT_REDACTED}$2`)
    out = out.replace(DATA_SENSITIVE_RE, `$1${SNAPSHOT_REDACTED}$3`)

    // 4. Authorization material scrub (defense in depth — cookies/authorization
    //    must never ride a snapshot).
    out = out.replace(AUTH_MATERIAL_RE, (_match, bearerPrefix: string | undefined) =>
      bearerPrefix ? `${bearerPrefix}${SNAPSHOT_REDACTED}` : SNAPSHOT_REDACTED)

    // 5. Hard cap with explicit marker.
    return capSnapshot(out, maxLength)
  } catch {
    return SNAPSHOT_SANITIZE_FAILED
  }
}
